import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useLocalStorage} from '../hooks/useLocalStorage';
import {getExpirationTime, isValidSession, normalizeSession} from '../utils/session';
import {environment} from '../../config/environment';

const AuthContext = createContext(null);
const demoUser = {
    id: 'demo-user',
    name: 'Cliente Demo',
    username: 'demo',
    email: 'demo@busifarm.com.br',
    accessToken: 'demo-token',
    tenantId: 'tenant-demo',
    companyId: 'company-demo',
    expiresIn: Date.now() + 8 * 60 * 60 * 1000,
};

export function AuthProvider({ children }) {
    const [userLogged, setUserLogged] = useLocalStorage('userLogged', null);
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(environment.disableAuth);
    const validAuthenticated = environment.disableAuth || (authenticated && isValidSession(userLogged));

    useEffect(() => {
        if (environment.disableAuth) {
            setUserLogged(demoUser);
            setAuthenticated(true);
            setLoading(false);
            return;
        }

        const userRecovered = localStorage.getItem('userLogged');
        if (userRecovered) {
            try {
                const parsedUser = normalizeSession(JSON.parse(userRecovered));
                if (isValidSession(parsedUser)) {
                    setUserLogged(parsedUser);
                    setAuthenticated(true);
                } else {
                    setUserLogged(null);
                    setAuthenticated(false);
                }
            } catch {
                setUserLogged(null);
                setAuthenticated(false);
            }
        } else {
            setAuthenticated(false);
        }
        setLoading(false);
    }, [setUserLogged]);

    const login = useCallback((user) => {
        if (environment.disableAuth) {
            setUserLogged({...demoUser, ...user});
            setAuthenticated(true);
            return;
        }

        const session = normalizeSession(user);
        if (isValidSession(session)) {
            setUserLogged(session);
            setAuthenticated(true);
        } else {
            setUserLogged(null);
            setAuthenticated(false);
        }
    }, [setUserLogged]);

    const logout = useCallback(() => {
        if (environment.disableAuth) {
            setUserLogged(demoUser);
            setAuthenticated(true);
            return;
        }

        setUserLogged(null);
        setAuthenticated(false);
    }, [setUserLogged]);

    useEffect(() => {
        if (environment.disableAuth) return undefined;
        if (!authenticated || !userLogged) return undefined;

        const expirationTime = getExpirationTime(userLogged);
        if (!expirationTime || expirationTime <= Date.now()) {
            logout();
            return undefined;
        }

        const timeoutId = window.setTimeout(logout, expirationTime - Date.now());
        return () => window.clearTimeout(timeoutId);
    }, [authenticated, logout, userLogged]);

    return (
        <AuthContext.Provider
            value={{
                userLogged,
                login,
                logout,
                authenticated: validAuthenticated,
                loading,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext);
